import { IconButton, Menu, Portal } from '@chakra-ui/react'
import type { ElementType } from 'react'
import { LuMonitor, LuMoon, LuSun } from 'react-icons/lu'
import { useT } from '~/i18n/use-t'
import { useThemePreference, type ThemePreference } from '~/theme/color-mode'

const OPTIONS: Array<[ThemePreference, ElementType]> = [
  ['system', LuMonitor],
  ['light', LuSun],
  ['dark', LuMoon],
]

/** System / Light / Dark. The button's name says what is chosen now ("Theme: System"). */
export function ThemeControl({ size = 'sm' }: { size?: 'xs' | 'sm' | 'md' }) {
  const { t } = useT()
  const { preference, setPreference } = useThemePreference()
  const Current = OPTIONS.find(([v]) => v === preference)![1]
  return (
    <Menu.Root positioning={{ placement: 'bottom-end' }}>
      <Menu.Trigger asChild>
        <IconButton
          variant='ghost'
          size={size}
          flexShrink={0}
          // A 48 x 48 touch target (the member pages' floor), whatever the icon size.
          minW='12'
          minH='12'
          aria-label={t('common.theme.current', { choice: t(`common.theme.${preference}`) })}
        >
          <Current aria-hidden />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW='44'>
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel fontSize='xs' color='fg.muted'>
                {t('common.theme.label')}
              </Menu.ItemGroupLabel>
              {OPTIONS.map(([value, Icon]) => (
                <Menu.Item
                  key={value}
                  value={value}
                  onClick={() => setPreference(value)}
                  data-selected={value === preference ? '' : undefined}
                  aria-current={value === preference ? 'true' : undefined}
                  fontWeight={value === preference ? 'medium' : 'normal'}
                  // 48 px rows on the member pages (the admin area keeps its compact controls).
                  minH='var(--ipc-control-h)'
                  px={3}
                >
                  <Icon aria-hidden />
                  {t(`common.theme.${value}`)}
                  {value === preference && <Menu.ItemIndicator />}
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}
